import { WEBGL_AUTH_MESSAGE, type WebglAuthMessage } from "./webgl-auth-message.js";

/**
 * The POPUP half of the panel's sign-in (ADR-050 D3, D5) — a relay that holds nothing.
 *
 * It reads `code` / `state` / `error` from its own query string, hands them to its opener, and closes. There is no
 * adapter here and no cryptography: the PKCE verifier lives in the opener, which is the document that survives the
 * flow.
 *
 * **That is not a stylistic choice — it is the fix for the plan panel's consensus finding.** Running the exchange
 * here would mean running it through `keycloak-js`, which persists `code_verifier` across the top-level navigation
 * (`lib/keycloak.js:1283`/`:1292`) into `localStorage` — falsifying the memory-only guarantee this whole module
 * exists to keep (`auth-tenancy.md` §5).
 *
 * It also makes ADR-050 D6 structural rather than guarded. Had the adapter processed this callback, an
 * `error_description=authentication_expired` response would re-enter `login()` directly, bypassing `onAuthError`
 * (`:1147`), and the browser adapter's `login` is `window.location.assign(...)` (`:317`) — an immediate
 * re-navigation to the auth endpoint with no new user gesture, invisible inside a preloaded iframe. With no
 * adapter in this document there is no such path to intercept: an error is classified here and posted as a
 * failure, and the user sees it.
 */

/** Marks this document as the callback. `registry.ts` matches the SAME key to serve it in place on an alias. */
export const WEBGL_AUTH_CALLBACK_PARAM = "arc_webgl_auth";

/**
 * The URL the popup is sent back to: this document's origin and path, with the query replaced.
 *
 * Built from the panel's own location rather than from config so the alias host AND the tenant's path prefix are
 * preserved by construction (ADR-050 D5) — `xrarea.arcreator.at/lumpi` must stay `xrarea.arcreator.at/lumpi`, or
 * the edge resolves a different tenant and the popup lands on a different origin.
 *
 * The same string is used for `redirect_uri` at BOTH the authorize and the token call, because Keycloak compares
 * them byte-for-byte; deriving it twice would be the classic way to get a `invalid_grant` that reads like a
 * credential problem.
 */
export function webglAuthCallbackUrl(href: string): string {
  const u = new URL(href);
  u.search = `?${WEBGL_AUTH_CALLBACK_PARAM}=1`;
  u.hash = "";
  return u.toString();
}

/** Is THIS document the callback? Read once at boot, before anything mounts. */
export function isWebglAuthCallbackDocument(search: string): boolean {
  if (search === "") return false;
  return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get(WEBGL_AUTH_CALLBACK_PARAM) === "1";
}

/**
 * Read what Keycloak appended. Returns null when `state` is missing — without it the opener cannot match the
 * response to its request, so there is nothing safe to relay.
 */
export function parseWebglAuthCallback(search: string): WebglAuthMessage | null {
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const state = q.get("state");
  if (!state) return null;

  const code = q.get("code");
  const error = q.get("error");
  // Neither present ⇒ not an OAuth response at all. Relaying an empty result would resolve the opener's wait with
  // a success that carries no code.
  if (!code && !error) return null;

  return {
    type: WEBGL_AUTH_MESSAGE,
    state,
    ...(code ? { code } : {}),
    ...(error ? { error } : {}),
    ...(q.get("error_description") ? { errorDescription: q.get("error_description") as string } : {}),
  };
}

/**
 * Hand the result to the opener and close.
 *
 * `targetOrigin` is this document's own origin, never `"*"`: the message carries an authorization code, and a
 * wildcard would post it to whatever happens to be listening if the opener ever moved. Returns whether a relay
 * actually happened, so the caller can tell "done" from "opened directly in a tab".
 *
 * **It does NOT close itself, and that is the diff panel's consensus finding.** The first version posted and then
 * called `win.close()` on the next line. `postMessage` delivery is queued as a task while `closed` flips
 * SYNCHRONOUSLY, so the opener's 400 ms closed-poll could observe a closed popup before draining the queued
 * message — cancelling a sign-in that had in fact succeeded, and discarding the code with it. The author sees a
 * spurious "cancelled" on a completed authentication.
 *
 * Closing is therefore the OPENER's job, once it has accepted the message (`webgl-popup-auth.ts`). That removes
 * the race rather than timing around it: with no self-close, a closed popup means the user closed it, which is
 * exactly what the poll is there to detect.
 */
export function relayWebglAuthCallback(message: WebglAuthMessage, win: Window = window): boolean {
  const opener = win.opener as Window | null;
  if (!opener) return false;
  opener.postMessage(message, win.location.origin);
  return true;
}
